import { useCallback, useEffect, useState } from 'react'
import Seo from '../components/Seo'
import MarketingNav from '../components/MarketingNav'
import MarketingFooter from '../components/MarketingFooter'
import PageHero from '../components/ds/PageHero'
import Section from '../components/ds/Section'
import { H2 } from '../components/ds/SectionHead'
import CtaBand from '../components/ds/CtaBand'
import Button from '../components/ds/Button'
import Card from '../components/ds/Card'
import { fetchStatusFeed, STATUS_PAGE_URL } from '../lib/statusFeed'

// /status — the page both apps open from their "trouble connecting" screens
// (EXPO_PUBLIC_STATUS_URL). It reads an EXTERNAL status feed, not our own API,
// which is the point: a page that goes down with the thing it reports on is
// worse than no page.
//
// ⛔ Status colour is the one place a hue carries meaning on this site, so it is
// a muted badge tint, never the action blue and never the brand turquoise. Same
// rule as the admin console: pale background, darker text of the same hue.
// Every value is a token in index.css (--color-success, --color-warning,
// --color-danger and their tints). Maintenance and checking are NEUTRAL, the
// apps' own rule for a status that is not good or bad news (2026-09-30).

const TONE = {
  operational: { label: 'All good',    cls: 'bg-success-tint text-success',      dot: 'bg-success' },
  degraded:    { label: 'Degraded',    cls: 'bg-warning-tint text-warning',      dot: 'bg-warning' },
  outage:      { label: 'Outage',      cls: 'bg-danger-tint text-danger',        dot: 'bg-danger' },
  maintenance: { label: 'Maintenance', cls: 'bg-surface-mute text-neutral-ink',  dot: 'bg-ink' },
  checking:    { label: 'Checking',    cls: 'bg-surface-mute text-neutral-ink',  dot: 'bg-ink-muted' },
}

const HEADLINE = {
  operational: 'Everything is working.',
  degraded:    'Something is slower than it should be.',
  outage:      'Something is down and we are on it.',
  maintenance: 'Planned maintenance is running.',
  checking:    'Checking…',
  unreachable: 'We cannot reach the status feed.',
}

function fmtWhen(d) {
  try {
    return new Date(d).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  } catch { return '' }
}

function Pill({ tier }) {
  const t = TONE[tier] ?? TONE.operational
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[13px] font-semibold ${t.cls}`}>
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${t.dot}`} />
      {t.label}
    </span>
  )
}

export default function ServiceStatus() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    const feed = await fetchStatusFeed()
    setData(feed)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [load])

  const reachable    = !!data?.reachable
  const components   = data?.components || []
  const incidents    = data?.incidents || []
  const maintenances = data?.maintenances || []
  const overall      = loading ? 'checking' : (data?.overall || 'degraded')
  const headline     = loading ? HEADLINE.checking : (!reachable ? HEADLINE.unreachable : (HEADLINE[overall] ?? HEADLINE.operational))

  return (
    <div className="bg-canvas">
      <Seo
        title="Service status"
        description="Live operational status of TempDash: payments and payouts, messaging, email, support and the app, plus any active incident or planned maintenance."
        path="/status"
      />
      <MarketingNav />

      {/* ⚠️ The ONE main landmark. Without it every <section> on the page
          sits outside any landmark and a screen reader user navigating by
          landmark cannot reach the content at all. The skip link in
          MarketingNav targets this id. */}
      <main id="main" tabIndex={-1} className="outline-none">

      <PageHero eyebrow="Service status" title={headline}>
        {!loading && reachable && <Pill tier={overall} />}
      </PageHero>

      {(incidents.length > 0 || maintenances.length > 0) && (
        <Section ground="canvas" width="text">
          <H2 size="md">Happening now</H2>
          <ul className="mt-6 space-y-3">
            {[...incidents, ...maintenances].map((inc) => (
              <Card as="li" key={inc.id ?? inc.name}>
                <div className="flex flex-wrap items-center gap-3">
                  <Pill tier={inc.tier ?? 'outage'} />
                  <span className="text-[14px] text-ink-muted">{fmtWhen(inc.startedAt ?? inc.createdAt)}</span>
                </div>
                <h3 className="mt-3 text-[19px] font-semibold">{inc.name}</h3>
                {inc.body && <p className="mt-2 text-[17px] leading-relaxed text-ink-muted">{inc.body}</p>}
              </Card>
            ))}
          </ul>
        </Section>
      )}

      <Section ground="surface" width="text">
        <H2 size="md">The parts</H2>
        {components.length === 0 ? (
          <p className="mt-5 text-[18px] text-ink-muted">
            {loading
              ? 'Reading the status feed.'
              : 'We cannot read the status feed right now. That does not mean anything is wrong with TempDash itself.'}
          </p>
        ) : (
          <ul className="mt-6 divide-y divide-hairline border-y border-hairline">
            {components.map((c) => (
              <li key={c.id ?? c.name} className="flex items-center justify-between gap-6 py-4">
                <span className="text-[18px] text-ink">{c.name}</span>
                <Pill tier={c.tier ?? 'operational'} />
              </li>
            ))}
          </ul>
        )}
        <p className="mt-8 text-[15px] text-ink-muted">
          This page reads an independent status feed, so it keeps working when TempDash does not.
          It refreshes itself every 30 seconds.
        </p>
      </Section>

      <CtaBand title="Something wrong that’s not listed?">
        <Button href="mailto:support@tempdash.app">Tell us</Button>
        {STATUS_PAGE_URL && (
          <Button href={STATUS_PAGE_URL} variant="outline">Full status history</Button>
        )}
      </CtaBand>

      </main>

      <MarketingFooter />
    </div>
  )
}
